import { REPORTING_YEAR_LOOKBACK, VERSION_HISTORY_COPY } from "../constants/versionHistory.constants";
import { VersionHistoryGroup, VersionHistoryRecord } from "../types/versionHistory.types";

export const findActiveVersionId = (versions: VersionHistoryGroup[]): string | null =>
  versions.find(version => version.isDefault)?.versionId ?? null;

/** `""` for a null version, so callers that already null-guard elsewhere can use it unconditionally. */
export const resolveVersionLabel = (
  version: Pick<VersionHistoryGroup, "versionName" | "versionNumber"> | null | undefined
): string => (version ? version.versionName ?? `Version ${version.versionNumber}` : "");

/**
 * The reason shown in the change-reason box for the selected calculation: that calculation's own
 * edit reason when one was logged, else the version's own (creation) reason. A version can be
 * updated and recalculated more than once, and each calculation's real trigger can differ from
 * the reason the version was originally created with -- see resolveRecordReason on the backend,
 * which resolves `record.reason` from changereasonlogs by that calculation's own time window.
 */
export const resolveDisplayedReason = (
  record: Pick<VersionHistoryRecord, "reason"> | null | undefined,
  version: Pick<VersionHistoryGroup, "reason"> | null | undefined
): string | null => record?.reason ?? version?.reason ?? null;

/** Descending, current year first, `REPORTING_YEAR_LOOKBACK` years back plus one year ahead. */
export const buildReportingYearOptions = (referenceYear: number = new Date().getFullYear()): number[] => {
  const years: number[] = [];
  for (let year = referenceYear + 1; year >= referenceYear - REPORTING_YEAR_LOOKBACK; year--) {
    years.push(year);
  }
  return years;
};

export const resolveVisibleChanges = (
  changes: string[] | undefined,
  showAll: boolean,
  maxVisible: number
): string[] => {
  if (!changes) return [];
  return showAll ? changes : changes.slice(0, maxVisible);
};

const HISTORY_FALLBACK = "-";

export const formatCalculatedVersionLabel = (calculateVersion: string | undefined): string =>
  calculateVersion ? `${VERSION_HISTORY_COPY.calculatedVersionPrefix} ${calculateVersion}` : HISTORY_FALLBACK;

export const collectVersionNames = (versions: VersionHistoryGroup[], excludeVersionId?: string | null): string[] =>
  versions
    .filter(version => version.versionId !== excludeVersionId)
    .map(version => version.versionName)
    .filter((name): name is string => !!name);
